/**
 * Argus — Auth Zustand store.
 *
 * State pur in-memory pentru:
 *   - user info curent (după login sau /auth/me)
 *   - csrfToken (din login response sau /auth/me)
 *   - isHydrated flag — false până la prima fetch /auth/me la mount RequireAuth.
 *
 * NU se persistă în localStorage (risc XSS expune CSRF token).
 * La refresh, store-ul revine la inițial; RequireAuth face fetch /auth/me.
 */

import { create } from 'zustand';

export type UserRole = 'admin' | 'operator' | 'viewer' | 'dpo';

export interface User {
  readonly id: string;
  readonly username: string;
  readonly email: string;
  readonly role: UserRole;
  readonly fullName: string | null;
}

export interface UserPermission {
  readonly id: number;
  readonly agentId: string;
  readonly agentHostname: string;
  readonly permission: 'read' | 'command' | 'admin';
  readonly grantedAt: string;
  readonly grantedBy: string;
}

interface AuthState {
  user: User | null;
  csrfToken: string | null;
  permissions: UserPermission[];
  isAuthenticated: boolean;
  isHydrated: boolean;
  setAuth: (user: User, csrfToken: string, permissions: UserPermission[]) => void;
  setPermissions: (permissions: UserPermission[]) => void;
  clearAuth: () => void;
  markHydrated: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  csrfToken: null,
  permissions: [],
  isAuthenticated: false,
  isHydrated: false,
  setAuth: (user, csrfToken, permissions) =>
    set({ user, csrfToken, permissions, isAuthenticated: true, isHydrated: true }),
  setPermissions: (permissions) => set({ permissions }),
  clearAuth: () =>
    set({ user: null, csrfToken: null, permissions: [], isAuthenticated: false, isHydrated: true }),
  markHydrated: () => set({ isHydrated: true }),
}));
